import { roundToTwoDecimals } from '@fruitful/fruitils';

import { PortfolioCalculator } from '../portfolio/PortfolioCalculator';
import { CORE_8_PORTFOLIO } from '../portfolio/portfolios/Core8Portfolio';
import {
    type MemberInput,
    type RetirementAccount,
    RetirementAccountType,
    type RetirementAssets,
    type RetirementContributions,
    type RetirementContributionWaterfall,
    YearType,
} from './types';

// Calculates how a total annual contribution to retirement should be allocated across a set of retirement accounts.
export class RetirementContributionCalculator {
    private static getDefaultEquitiesPercentage(): number {
        const allocations =
            PortfolioCalculator.calculateAllocations(CORE_8_PORTFOLIO);
        return allocations.equities;
    }

    constructor(
        private readonly totalAnnualContribution: number,
        private readonly waterfall: RetirementContributionWaterfall,
        private readonly memberInputs: MemberInput[],
        private readonly retirementAssets: RetirementAssets,
        private readonly currentYear: number, // Add current year to calculate member ages
    ) {}

    public calculate(): RetirementContributions {
        const retirementContributions: RetirementContributions = [];
        let remainingContribution = this.totalAnnualContribution;
        let waterfallIndex = 0;

        while (
            remainingContribution > 0 &&
            waterfallIndex < this.waterfall.length
        ) {
            // get the next step in the waterfall
            const waterfallStep = this.waterfall[waterfallIndex];
            if (!waterfallStep) {
                break;
            }
            const { memberId, contributions } = waterfallStep;

            // determine the total amount to be contributed to this waterfall step
            const totalContributionLimit = roundToTwoDecimals(
                Object.keys(contributions).reduce(
                    (acc, accountType) =>
                        acc +
                        this.getAnnualContributionLimit(
                            memberId,
                            accountType as RetirementAccountType,
                        ),
                    0,
                ),
            );
            const waterfallStepContribution = roundToTwoDecimals(
                Math.min(remainingContribution, totalContributionLimit),
            );

            // split the employee contribution across the account types in the waterfall step
            const accountContributions = Object.entries(contributions).map(
                ([accountType, contributionPercentage]: [string, number]) => {
                    const account = this.getAccount(
                        memberId,
                        accountType as RetirementAccountType,
                    );
                    const employeeContribution = roundToTwoDecimals(
                        waterfallStepContribution * contributionPercentage,
                    );

                    const employerContribution = this.getEmployerContribution(
                        memberId,
                        employeeContribution,
                        accountType as RetirementAccountType,
                    );

                    return {
                        account,
                        employeeContribution,
                        employerContribution,
                        totalContribution: roundToTwoDecimals(
                            employeeContribution + employerContribution,
                        ),
                    };
                },
            );

            retirementContributions.push(...accountContributions);

            remainingContribution = roundToTwoDecimals(
                remainingContribution - waterfallStepContribution,
            );
            waterfallIndex++;
        }

        return retirementContributions;
    }

    /**
     * Check if a member can contribute to a specific retirement account type
     * based on whether they are still in the accumulation phase (before retirement age)
     */
    private canContributeToAccount(
        memberId: string,
        accountType: RetirementAccountType,
    ): boolean {
        const member = this.memberInputs.find((m) => m.id === memberId);
        if (!member) {
            return false;
        }

        const memberAge = member.currentAge + this.currentYear;
        const yearType = this.getYearType(memberAge, member);

        // Only allow contributions if member is in accumulation phase (still working, before retirement age)
        if (yearType === YearType.ACCUMULATION) {
            // For employer plans, also check if member still has access to the plan
            if (
                accountType === RetirementAccountType.EMPLOYER_401K ||
                accountType === RetirementAccountType.ROTH_401K
            ) {
                return member.hasEmployerRetirementPlan;
            }

            // For self-employed plans, check if member is still self-employed
            if (
                accountType === RetirementAccountType.SEP_IRA ||
                accountType === RetirementAccountType.SOLO_401K
            ) {
                return member.isSelfEmployed;
            }

            // For all other account types, allow contributions during accumulation phase
            return true;
        }

        // No contributions allowed during decumulation (retirement) or end of life phases
        return false;
    }

    /**
     * Get the year type for a member at a specific age
     */
    private getYearType(age: number, member: MemberInput): YearType {
        if (age >= member.lifeExpectancy) return YearType.END_OF_LIFE;
        if (age >= member.retirementAge) return YearType.DECUMULATION;
        return YearType.ACCUMULATION;
    }

    private getAccount(
        memberId: string,
        accountType: RetirementAccountType,
    ): RetirementAccount {
        const existingAccount = this.retirementAssets.find((asset) => {
            return asset.type === accountType && asset.owner === memberId;
        });

        if (existingAccount != null) {
            return existingAccount;
        }

        return {
            id: `new-${memberId}-${accountType}`,
            owner: memberId,
            type: accountType,
            balance: 0,
            equitiesPercentage:
                RetirementContributionCalculator.getDefaultEquitiesPercentage(),
            costBasis: 0, // New accounts start with zero cost basis
        };
    }

    private getAnnualContributionLimit(
        memberId: string,
        accountType: RetirementAccountType,
    ): number {
        // Check if member can contribute to this account type
        if (!this.canContributeToAccount(memberId, accountType)) {
            return 0;
        }

        const member = this.memberInputs.find((m) => m.id === memberId);
        if (!member) {
            throw new Error(`Member ${memberId} not found`);
        }

        switch (accountType) {
            case RetirementAccountType.EMPLOYER_401K:
                return 23_000;
            case RetirementAccountType.ROTH_401K:
                return 23_000;
            case RetirementAccountType.SEP_IRA:
                return roundToTwoDecimals(
                    Math.min(0.25 * member.currentGrossAnnualIncome, 69_000),
                );
            case RetirementAccountType.SOLO_401K:
                return 23_000;
            case RetirementAccountType.TRAD_IRA:
                return 7_000;
            case RetirementAccountType.BACKDOOR_ROTH_IRA:
                return 7_000;
            case RetirementAccountType.TAXABLE_BROKERAGE:
                return 999_999;
            default:
                return 0;
        }
    }

    private getEmployerContribution(
        memberId: string,
        employeeContribution: number,
        accountType: RetirementAccountType,
    ): number {
        // Check if member can contribute to this account type
        if (!this.canContributeToAccount(memberId, accountType)) {
            return 0;
        }

        const member = this.memberInputs.find((m) => m.id === memberId);
        if (!member) {
            throw new Error(`Member ${memberId} not found`);
        }

        if (
            !member.hasEmployerRetirementPlan ||
            member.employerMatchMaximumPercentOfIncome == null ||
            member.employerMatchPercentage == null
        ) {
            return 0;
        }

        const employerMatchMaximum = roundToTwoDecimals(
            member.employerMatchMaximumPercentOfIncome *
                member.currentGrossAnnualIncome,
        );

        const employerMatch = roundToTwoDecimals(
            Math.min(employeeContribution, employerMatchMaximum) *
                member.employerMatchPercentage,
        );

        switch (accountType) {
            case RetirementAccountType.EMPLOYER_401K:
            case RetirementAccountType.ROTH_401K:
                return employerMatch;
            default:
                return 0;
        }
    }
}
